import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as estimatesApi from '../api';
import type { Estimate, EstimateDetail, EstimateListFilters, LifecycleAction, SaveEstimateBody } from '../types';

export const estimateKeys = {
  all: ['estimates'] as const,
  lists: () => [...estimateKeys.all, 'list'] as const,
  list: (filters: EstimateListFilters) => [...estimateKeys.lists(), filters] as const,
  detail: (id: string) => [...estimateKeys.all, 'detail', id] as const,
};

export function useEstimateList(filters: EstimateListFilters) {
  return useQuery({ queryKey: estimateKeys.list(filters), queryFn: () => estimatesApi.listEstimates(filters), placeholderData: keepPreviousData });
}

export function useEstimate(id: string | undefined) {
  return useQuery({ queryKey: estimateKeys.detail(id ?? ''), queryFn: () => estimatesApi.getEstimate(id as string), enabled: !!id });
}

/** Writes a fresh estimate row into the cached detail (its price book and company stay) and refreshes lists. */
function useStoreEstimate() {
  const queryClient = useQueryClient();
  return (estimate: Estimate) => {
    queryClient.setQueryData<EstimateDetail>(estimateKeys.detail(estimate.id), (old) => (old ? { ...old, estimate } : old));
    void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
  };
}

export function useCreateEstimate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opportunityId: string) => estimatesApi.createEstimate(opportunityId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: estimateKeys.lists() }),
  });
}

export function useSaveEstimate(id: string) {
  const store = useStoreEstimate();
  return useMutation({ mutationFn: (body: SaveEstimateBody) => estimatesApi.saveEstimate(id, body), onSuccess: store });
}

export type EstimateCommand = { action: LifecycleAction; comment?: string } | { action: 'reprice' } | { action: 'revise' };

/** Lifecycle actions, reprice and revise. Revise resolves with the new revision (another id). */
export function useEstimateCommand(id: string) {
  const queryClient = useQueryClient();
  const store = useStoreEstimate();
  const refetchDetail = () => queryClient.invalidateQueries({ queryKey: estimateKeys.detail(id) });
  return useMutation({
    mutationFn: (command: EstimateCommand): Promise<Estimate> => {
      if (command.action === 'reprice') return estimatesApi.repriceEstimate(id);
      if (command.action === 'revise') return estimatesApi.reviseEstimate(id);
      return estimatesApi.runLifecycleAction(id, command.action, command.action === 'reject' ? { comment: command.comment ?? '' } : undefined);
    },
    onSuccess: (estimate, command) => {
      if (command.action === 'revise' || command.action === 'reprice') {
        // Revise supersedes this estimate; reprice switches its price book: refetch the whole detail.
        void refetchDetail();
        void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
        return;
      }
      store(estimate);
    },
    // A refused action (STALE, INVALID_STATE, ...) usually means the copy on screen is out of date.
    onError: () => refetchDetail(),
  });
}

export function useDeleteEstimate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => estimatesApi.deleteEstimate(id),
    onSuccess: () => {
      // Mark every cached estimate stale without refetching the deleted one (a deleted revision also restores its
      // superseded source on the server), and refresh the lists now.
      void queryClient.invalidateQueries({ queryKey: estimateKeys.all, refetchType: 'none' });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
    },
  });
}

export function useGenerateDeck(id: string) {
  const queryClient = useQueryClient();
  const store = useStoreEstimate();
  return useMutation({
    mutationFn: () => estimatesApi.generateDeck(id),
    onSuccess: ({ estimate }) => store(estimate),
    // A failed run records deckStatus/deckError on the estimate: refetch to show it.
    onError: () => queryClient.invalidateQueries({ queryKey: estimateKeys.detail(id) }),
  });
}
